'use client';

import { datadogRum } from '@datadog/browser-rum';
import { UserDetailsContext } from '@modules/app-router-utils/user-details-provider';
import { logger } from '@modules/logger';
import { UserProfileContext } from '@modules/user-profile/UserProfileProvider';
import type { UserDetails } from '@modules/user-profile/user-profile-details-validator';
import { getUserProfileFullName } from '@modules/user-profile/user-profile-helpers';
import { useContext, useEffect } from 'react';

const BLINQ_EMPLOYEE_EMAIL_PATTERN = /@blinq\.me$/i;

/**
 * Pushes the current user's identity and workspace context into Datadog RUM.
 *
 * Reads from whichever provider is mounted: `UserProfileContext` on the Pages
 * Router, `UserDetailsContext` on the App Router. Safe to call on unauthenticated
 * routes; it no-ops until a user with an `id` is available. If the user is
 * authenticated but somehow has no email, we still identify by `id` and emit a
 * warning (this shouldn't happen given Firebase Auth, but the schema allows it).
 *
 * RUM init itself is environment-gated inside `rum.ts` via `DEPLOYMENT_DOMAIN`, so
 * we don't gate again here. That keeps preview sessions identified, which the old
 * Pages-Router-only `Layout2` effect was missing.
 */
export function useRumIdentity(): void {
  const pagesProfile = useContext(UserProfileContext);
  const appUserDetails = useContext(UserDetailsContext);
  const userDetails: UserDetails | null =
    pagesProfile?.userProfileDetails ?? appUserDetails ?? null;

  useEffect(() => {
    if (!userDetails?.id) return;

    const fullName = getUserProfileFullName(userDetails);

    // `email` is nullable in the schema but, in practice, every authenticated
    // user should have one (Firebase Auth is the source of truth). Warn if we
    // hit this branch so we can investigate (e.g. legacy/migrated rows).
    if (!userDetails.email) {
      logger.warn('Authenticated user has no email; emitting reduced RUM identity', {
        module: 'monitoring',
        userId: userDetails.id,
      });
    }

    datadogRum.setUser({
      id: userDetails.id,
      ...(userDetails.email ? { email: userDetails.email } : {}),
      // Only set `name` when it's a real string. `getUserProfileFullName`
      // returns the JS value `undefined` for empty given/family names, and the
      // RUM SDK would otherwise serialise that to the literal string "undefined".
      ...(fullName ? { name: fullName } : {}),
    });

    if (userDetails.email) {
      datadogRum.setGlobalContextProperty(
        'is_blinq_employee',
        BLINQ_EMPLOYEE_EMAIL_PATTERN.test(userDetails.email),
      );
    }
    datadogRum.setGlobalContextProperty('plan', userDetails.currentWorkspace.plan.name);
    datadogRum.setGlobalContextProperty('workspace', {
      id: userDetails.currentWorkspace.id,
      role: userDetails.currentWorkspace.profile.role,
      type: userDetails.currentWorkspace.type,
    });
  }, [userDetails]);
}
